import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";

/** `note` — the ambient register: session weather that belongs in the
 * stream but must never compete with it.
 *
 * One primitive with two faces, not two primitives. Turn activity
 * ("✳ Distilling…"), mode and command notices, and stream dividers are the
 * same editorial thing — quiet, skippable, meaningful only in passing —
 * and giving each its own component made the family look larger than the
 * vocabulary actually is. A dialect quirk earns a face here, never a new
 * primitive.
 *
 *   text  — a one-line status or meta event, optionally live
 *   rule  — a divider between turns, optionally labelled
 *
 * ## Why the meter left
 *
 * There used to be a third face for the context budget, and it was in the
 * wrong place twice over. A meter is not an EVENT — it does not happen at
 * a point in the transcript, it is true continuously, and pinning one to
 * the row where it was last sampled tells the reader something about the
 * scrollback rather than about now. And it is not ambient: the number that
 * governs whether you can ask another question belongs where you ask it.
 *
 * It is now `std-context-meter`, mounted by the composer. See
 * `components/context-meter.ts`.
 *
 * ## Live and not live
 *
 * The `text` face has one variant and it matters: `active` marks the line
 * as something happening RIGHT NOW rather than something that happened.
 * Both vendor surfaces draw this the same way — a larger line with light
 * moving across it — because the reader's question at the bottom of a
 * stream is "is it still working?", and a static grey line answers "no"
 * whether or not that is true. So an active note steps up the ramp, drops
 * the italic that marks the quiet ones, and shimmers.
 *
 * A meta notice does none of that. "mode: plan" is not working on
 * anything, and giving every ambient line the live treatment would spend
 * the whole effect. */
@component({ name: "std-turn-note" })
export class TurnNote extends LonaWebComponent {
  private $text: HTMLElement = this.$("text");
  private $label: HTMLElement = this.$("label");

  static makeWith(props: TurnNote.Props): TurnNote {
    return (TurnNote.make() as TurnNote).bind(props);
  }

  bind(props: TurnNote.Props): this {
    this.setAttribute("face", props.face);
    if (props.face === "rule") {
      const label = props.label ?? "";
      this.$label.textContent = label;
      this.$label.style.display = label === "" ? "none" : "";
      // Closes the row's gap when there is nothing to sit in it — an
      // unlabelled divider was rendering as two rules with a 14px hole
      // where the label would have gone.
      this.toggleAttribute("labelled", label !== "");
      return this;
    }
    this.toggleAttribute("active", props.active ?? false);
    this.$text.textContent = props.text;
    return this;
  }

  static $styles = [
    css`
      :host {
        display: block;
        /* The rule face is a full-bleed divider, so the host must claim its
           column — a shrink-to-fit parent (a centered card, a flex row)
           would otherwise collapse the rule to the width of its label. */
        width: 100%;
      }
      #root {
        display: flex;
        align-items: center;
        gap: 7px;
      }
      #glyph,
      .rule,
      #label {
        display: none;
      }

      /* ---- text: an activity or meta line -------------------------- */
      :host([face="text"]) #glyph {
        display: inline-block;
        flex: none;
        --font-size: var(--turn-fine-size, var(--text-8px));
        color: var(--text-ter);
      }
      /* An ambient meta line is a fact ABOUT the turn, not one of its
         rows, so it sits in the fine register with the counts and the
         chevrons. */
      #text {
        min-width: 0;
        --font-size: var(--turn-fine-size, var(--text-8px));
        color: var(--text-ter);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      :host([face="text"]) #text {
        font-style: italic;
      }

      /* ---- text[active]: something is happening now ---------------- */
      /* Every size in this file is --font-size on a ramp element, which
         is the app's only way to set type. An earlier revision argued for
         a literal font-size here on the grounds that bare spans match no
         ramp selector — true, and the wrong conclusion: the fix is to stop
         using bare spans, not to leave the ramp. They are <p class="small">
         now, the same shape every dense row in this repo is built from. */
      :host([face="text"][active]) #glyph {
        --font-size: var(--small-size);
        color: var(--text-sec);
        animation: spin 2.8s linear infinite;
      }
      :host([face="text"][active]) #text {
        /* The live line is the one thing on screen actually happening, so
           it steps up to the reading size the reply itself is set in. */
        --font-size: var(--p-size);
        font-style: normal;
        font-weight: 450;
        /* The light sweeps across the words themselves: the gradient is
           painted, then clipped to the glyphs. The two ends of the ramp
           are the SAME tone, so the line at rest is a plain secondary
           line and only the crest is bright — a two-stop gradient pulses
           the whole line instead, which reads as a broken element. */
        background-image: linear-gradient(
          100deg,
          var(--text-ter, #8b93a7) 0%,
          var(--text-ter, #8b93a7) 38%,
          var(--text-pri, #1f2328) 50%,
          var(--text-ter, #8b93a7) 62%,
          var(--text-ter, #8b93a7) 100%
        );
        background-size: 300% 100%;
        background-clip: text;
        -webkit-background-clip: text;
        color: transparent;
        -webkit-text-fill-color: transparent;
        animation: sweep 2.6s linear infinite;
      }
      @keyframes sweep {
        from {
          background-position: 150% 0;
        }
        to {
          background-position: -50% 0;
        }
      }
      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }
      /* Motion is the whole signal here, so what replaces it has to carry
         the same fact: the live line stays a step up the ramp and takes
         the bright tone outright. */
      @media (prefers-reduced-motion: reduce) {
        :host([face="text"][active]) #glyph,
        :host([face="text"][active]) #text {
          animation: none;
        }
        :host([face="text"][active]) #text {
          background-image: none;
          color: var(--text-sec, #6a7186);
          -webkit-text-fill-color: var(--text-sec, #6a7186);
        }
      }

      /* ---- rule: a divider, optionally labelled -------------------- */
      :host([face="rule"]) #root {
        min-height: 8px;
      }
      :host([face="rule"]:not([labelled])) #root {
        gap: 0;
      }
      :host([face="rule"]) .rule {
        display: block;
        flex: 1;
        height: 1px;
        background: var(--divider, rgba(127, 127, 127, 0.35));
      }
      :host([face="rule"]) #label {
        display: block;
        flex: none;
        font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
        --font-size: var(--turn-fine-size, var(--text-8px));
        color: var(--text-ter);
        white-space: nowrap;
      }
      :host([face="rule"]) #text {
        display: none;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <div id="root">
      <p class="small" id="glyph">✳</p>
      <span class="rule"></span>
      <p class="small" id="label"></p>
      <p class="small" id="text"></p>
      <span class="rule"></span>
    </div>
  `;
}

export namespace TurnNote {
  export type Props =
    | {
      face: "text";
      text: string;
      /** True while the thing described is still happening. Steps the line
       * up the ramp and animates it; false for a notice about something
       * already settled. */
      active?: boolean;
    }
    | { face: "rule"; label?: string };
}
